import { useState } from "react";
import { Mail } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { FilterMenuBody } from "@/estimating/components/HeaderFilterMenu";

const SEP = "|";
// Type E bid rows email the GC(s); everything else emails the architect (server rule in approved-equal draft).
const toGc = (r) => String(r.budget_id).startsWith("bid:") && r.type === "E";
const gcRecipients = (r) => {
  const list = (r.gc_contacts || []).filter((c) => (c.email || "").trim());
  return list.length ? list : (r.gc_contact_email ? [{ gc: r.gc_name || "", name: r.gc_contact_name, email: r.gc_contact_email }] : []);
};

const iconBtn = "p-1.5 rounded-sm text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-30 disabled:pointer-events-none";

/** Several GC contacts → pick who gets it first (filter-menu picker; All = everyone). Each recipient gets their own email. */
function RecipientPicker({ r, list, onEmail, testid }) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState("");
  const picks = value ? value.split(SEP) : list.map((c) => c.email);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" title="Request Approval" className={iconBtn} data-testid={testid}><Mail className="w-4 h-4" /></button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-0" data-testid={`${testid}-picker`}>
        <div className="border-b border-slate-100 px-2.5 py-1.5 text-[11px] font-semibold text-muted-foreground">Send to which contacts?</div>
        <FilterMenuBody multi separator={SEP} value={value} onChange={setValue} close={() => setOpen(false)} testid={`${testid}-recipients`}
                        options={list.map((c) => ({ value: c.email, label: `${c.gc} — ${c.name || c.email}` }))} />
        <div className="border-t border-slate-100 p-2">
          <Button size="sm" disabled={!picks.length} onClick={() => { setOpen(false); onEmail({ ...r, recipients: picks }); }}
                  className="h-7 w-full bg-[#007BFF] text-xs text-white hover:bg-[#0069d9]" data-testid={`${testid}-continue`}>
            Draft {picks.length} email{picks.length !== 1 ? "s" : ""}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function EmailButton({ r, onEmail }) {
  const testid = `spec-email-${r.budget_id}-${r.scope_code}`;
  const list = toGc(r) ? gcRecipients(r) : [];
  const ok = toGc(r) ? list.length > 0 : !!(r.architect_contact_email || "").trim();
  if (ok && list.length > 1) return <RecipientPicker r={r} list={list} onEmail={onEmail} testid={testid} />;
  const btn = (
    <button type="button" disabled={!ok} onClick={() => onEmail(r)} data-testid={testid} title={ok ? "Request Approval" : undefined} className={iconBtn}>
      <Mail className="w-4 h-4" />
    </button>
  );
  if (ok) return <Tooltip><TooltipTrigger asChild>{btn}</TooltipTrigger><TooltipContent className="text-xs">Request Approval</TooltipContent></Tooltip>;
  return (
    <Tooltip>
      <TooltipTrigger asChild><span className="inline-block" data-testid={`spec-email-disabled-${r.budget_id}-${r.scope_code}`}>{btn}</span></TooltipTrigger>
      <TooltipContent className="text-xs">{toGc(r) ? "Add a GC contact email to request approval" : "Add the architect contact email to request approval"}</TooltipContent>
    </Tooltip>
  );
}
